import { useTranslation } from '../../../i18n';
import React, { useState, useEffect } from 'react';
import {
    Card,
    Table,
    Button,
    Space,
    message,
    Popconfirm,
    Tag
} from 'antd';
import {
    CloudDownloadOutlined,
    ReloadOutlined,
    HistoryOutlined,
    PlusOutlined
} from '@ant-design/icons';
import hostSharedService from '../../../../shared/services/hostSharedService';
import OffsiteBackups from './OffsiteBackups';

const BackupManager = ({ vhostId, vhostName, adminView = false }) => {
    const { t } = useTranslation();
    const [backups, setBackups] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (vhostId) {
            loadBackups();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vhostId]);

    const loadBackups = async () => {
        if (!vhostId) return;
        setLoading(true);
        try {
            const response = await hostSharedService.getBackups(vhostId);
            if (response.success) {
                setBackups(response.data || []);
            } else {
                message.error(response.error || response.message || t('Failed to load backup list'));
            }
        } catch (error) {
            console.error('[BackupManager] Error:', error);
            message.error(t('Failed to load backup list'));
        } finally {
            setLoading(false);
        }
    };

    const handleCreateBackup = async () => {
        try {
            const response = await hostSharedService.backupWeb(vhostName, true, '');
            if (response.success) {
                message.success(t('Backup task created'));
                loadBackups();
            } else {
                message.error(response.error || response.message || t('Failed to create backup'));
            }
        } catch (error) {
            message.error(t('Failed to create backup'));
        }
    };

    const handleRestoreBackup = async (record) => {
        try {
            const response = await hostSharedService.restoreWeb(vhostName, record.path, '', true);
            if (response.success) {
                message.success(t('Backup restore task created'));
            } else {
                message.error(response.error || response.message || t('Restore failed'));
            }
        } catch (error) {
            message.error(error.response?.data?.error || t('Restore failed'));
        }
    };

    const handleDownloadBackup = async (record) => {
        try {
            const url = await hostSharedService.fileDownloadUrl(vhostId, record.path);
            window.open(url, '_blank', 'noopener');
        } catch (error) {
            message.error(t('Download failed'));
        }
    };

    const columns = [
        {
            title: t('Backup name'),
            dataIndex: 'name',
            key: 'name',
            render: (name, record) => (
                <Space>
                    {name || '-'}
                    <Tag>{record.kind === 'database' ? t('Database') : t('Website')}</Tag>
                </Space>
            )
        },
        {
            title: t('Backup size'),
            dataIndex: 'size',
            key: 'size',
            render: (size) => (size ? `${(size / 1024 / 1024).toFixed(2)} MB` : '-')
        },
        {
            title: t('Created at'),
            dataIndex: 'created_at',
            key: 'created_at',
            render: (time) => time || '-'
        },
        {
            title: t('Actions'),
            key: 'action',
            render: (_, record) => (
                <Space>
                    {record.kind !== 'database' && (
                        <Popconfirm
                            title={t('Restore this backup?')}
                            description={t('Restoring will overwrite the current data')}
                            onConfirm={() => handleRestoreBackup(record)}
                        >
                            <Button size="small" icon={<HistoryOutlined />}>
                                {t('Restore')}
                            </Button>
                        </Popconfirm>
                    )}
                    <Button
                        size="small"
                        icon={<CloudDownloadOutlined />}
                        onClick={() => handleDownloadBackup(record)}
                    >
                        {t('Download')}
                    </Button>
                </Space>
            )
        }
    ];

    return (
        <>
        <Card
            title={t('Backup list')}
            extra={
                <Space>
                    <Button icon={<ReloadOutlined />} onClick={loadBackups}>
                        {t('Refresh')}
                    </Button>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleCreateBackup}
                    >
                        {t('Create backup')}
                    </Button>
                </Space>
            }
        >
            <Table
                dataSource={backups}
                columns={columns}
                rowKey="id"
                loading={loading}
                pagination={{
                    pageSize: 10,
                    showTotal: (total) => t('{{total}} backups total', { total: total })
                }}
            />
        </Card>
        <OffsiteBackups vhostId={vhostId} adminView={adminView} />
        </>
    );
};

export default BackupManager;
